import { defineStore } from 'pinia';
import { Config } from '@/config';
import apiService from '@/services/api.service';
import type { QuestionAnswerDto } from '@/services/types/api.types';

export interface QuestionAnswerState {
  questionAnswers: QuestionAnswerDto[];
  itemLoadingStates: Set<number>;
}

export const useQuestionAnswersStore = defineStore({
  id: 'questionAnswers',
  state: (): QuestionAnswerState => ({
    questionAnswers: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (projectModuleId: number) => state.itemLoadingStates.has(projectModuleId),
    questionAnswerList: (state) => {
      return [...state.questionAnswers].sort((a, b) => b.created_at.localeCompare(a.created_at));
    },
  },
  actions: {
    async fetchQuestionAnswersByModuleId(projectModuleId: number) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<QuestionAnswerDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/question-answers`,
        );
        this.questionAnswers = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },
    async saveQuestionAnswer(qa: QuestionAnswerDto) {
      try {
        await apiService.put<QuestionAnswerDto>(`${Config.api.v2}/question-answers/${qa.id}`, qa);
        await this.fetchQuestionAnswersByModuleId(qa.project_module_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async junkQuestionAnswer(qa: QuestionAnswerDto) {
      try {
        await apiService.post(`${Config.api.v2}/question-answers/${qa.id}/:junk`, {});
        await this.fetchQuestionAnswersByModuleId(qa.project_module_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async reopenQuestionAnswer(qa: QuestionAnswerDto) {
      try {
        await apiService.post(`${Config.api.v2}/question-answers/${qa.id}/:reopen`, {});
        await this.fetchQuestionAnswersByModuleId(qa.project_module_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
  },
});
